/**
 * Receipts.
 *
 * A receipt is text: shown on screen, shared over WhatsApp, which is how a Nicaraguan shop
 * actually sends anything, and printed on a thermal printer where there is one (`imprimir.ts`).
 * Thirty-two characters wide, which is exactly a line of a 58mm roll and still reads in a chat
 * bubble.
 */

import { ZONA_HORARIA, centavos, formatMoney, formatQuantity, fromUnits, type Centavos, type Settings } from '@abastece/shared';
import type { Pedido, Venta } from '@abastece/core';
import { ahorroDe, ahorroDeLinea } from './ofertas.js';

/** A line of a 58mm roll, in characters: what every receipt, statement and pay stub is cut to. */
export const ANCHO = 32;
/** A line of one base unit each: the bottle, the pound. Anything else is a presentation. */
const UNA_UNIDAD = fromUnits(1);

/**
 * What the shop says about itself on a receipt, as the owner sets it in «Configuración»: its name
 * on top, then whichever of an address, a phone and a RUC it has, and a last line of its own.
 */
export interface DatosNegocio {
  nombre: string;
  direccion?: string;
  telefono?: string;
  ruc?: string;
  /** The receipt's last line. Absent or empty, there is none. */
  pie?: string;
}

export const datosNegocio = (c: Settings): DatosNegocio => ({
  nombre: c.negocioNombre,
  direccion: c.negocioDireccion,
  telefono: c.negocioTelefono,
  ruc: c.negocioRuc,
  pie: c.reciboPie,
});

/**
 * Text cut into lines that fit the roll, at the spaces. The page prints with `white-space: pre`,
 * so a line longer than the roll is not wrapped on paper, it is cut off at the edge — an address
 * that loses its landmark. A word too long for any line (a long email) is split where it must be.
 */
export function envolver(texto: string, ancho = ANCHO): string[] {
  const lineas: string[] = [];
  let linea = '';
  for (let palabra of texto.split(/\s+/).filter(Boolean)) {
    if (linea && linea.length + 1 + palabra.length <= ancho) {
      linea += ` ${palabra}`;
      continue;
    }
    if (linea) lineas.push(linea);
    while (palabra.length > ancho) {
      lineas.push(palabra.slice(0, ancho));
      palabra = palabra.slice(ancho);
    }
    linea = palabra;
  }
  if (linea) lineas.push(linea);
  return lineas;
}

const FORMAS: Record<string, string> = {
  efectivo_cordobas: 'Efectivo C$',
  efectivo_dolares: 'Efectivo US$',
  transferencia: 'Transferencia',
  billetera: 'Billetera',
  tarjeta: 'Tarjeta',
  credito: 'Fiado',
};

/**
 * A label and its amount on one line, the amount to the right. When they do not fit, the label
 * goes on lines of its own, cut at the spaces and indented as it was, and the amount at the right
 * of its last line, or under it. A long name used to run past the roll, and the page prints with
 * `white-space: pre`: the amount was cut off at the edge (2026-10-13).
 */
export function fila(izq: string, der: string): string {
  if (izq.length + 1 + der.length <= ANCHO) return izq + ' '.repeat(ANCHO - izq.length - der.length) + der;
  const sangria = /^ */.exec(izq)![0];
  const arriba = envolver(izq, ANCHO - sangria.length).map((x) => sangria + x);
  const ultima = arriba.at(-1) ?? '';
  const caben = ultima.length + 1 + der.length <= ANCHO;
  if (arriba.length > 1 && caben) return [...arriba.slice(0, -1), ultima + ' '.repeat(ANCHO - ultima.length - der.length) + der].join('\n');
  return [...arriba, der.padStart(ANCHO)].join('\n');
}

export interface OpcionesRecibo {
  /**
   * When the sale happened, as the ISO timestamp its event carries. A receipt sent again later
   * used to print the moment it was sent; absent, it is now, which is right for a fresh sale.
   */
  hora?: string;
  /** Who it was for, and — for fiado — what they owe once this sale is counted. */
  cliente?: { nombre: string; saldo?: number | null };
  /**
   * The name of each presentation on the sale, by id. A line of anything but one base unit says
   * which it was: «1 Fardo x 480.00», where it said «1 x 480.00» and a case read as one bottle at
   * the price of six.
   */
  presentaciones?: ReadonlyMap<string, string>;
}

/** The top of a receipt: the shop's name, then whichever of the rest it has given. A pay stub's too (`colilla.ts`). */
export function encabezado(negocio: DatosNegocio): string[] {
  const l = envolver(negocio.nombre.toUpperCase());
  if (negocio.direccion?.trim()) l.push(...envolver(negocio.direccion));
  if (negocio.telefono?.trim()) l.push(...envolver(`Tel: ${negocio.telefono.trim()}`));
  if (negocio.ruc?.trim()) l.push(...envolver(`RUC: ${negocio.ruc.trim()}`));
  return l;
}

/**
 * The bottom: a blank line and the shop's own words, if it has any.
 *
 * The receipt is for the customer standing at the counter, so it is Spanish whatever language
 * the person holding the phone has chosen; the same goes for the date format. The last line is
 * the shop's own, «¡Gracias por su compra!» until it says something else.
 */
function pie(negocio: DatosNegocio): string[] {
  const lineas = envolver(negocio.pie ?? '');
  return lineas.length > 0 ? ['', ...lineas] : [];
}

const fecha = (hora?: string) =>
  (hora ? new Date(hora) : new Date()).toLocaleString('es-NI', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: ZONA_HORARIA,
  });

/** A day the books keep as «2026-10-13», as the receipt's date says one: «13/10/26». */
const dia = (fecha: string) =>
  new Date(`${fecha}T12:00:00Z`).toLocaleDateString('es-NI', { dateStyle: 'short', timeZone: 'UTC' });

/**
 * A receipt for the shop to see its own details on before any sale prints them: the top and the
 * bottom exactly as they will come out, and one line of something sold in between.
 */
export function muestraRecibo(negocio: DatosNegocio): string {
  return [
    ...encabezado(negocio),
    'Ticket AA-000123',
    fecha(),
    '-'.repeat(ANCHO),
    'Arroz 80/20',
    fila(
      `  2 x ${formatMoney(2_500 as Centavos, { symbol: false })}`,
      formatMoney(5_000 as Centavos, { symbol: false }),
    ),
    '-'.repeat(ANCHO),
    fila('TOTAL', formatMoney(5_000 as Centavos)),
    ...pie(negocio),
  ].join('\n');
}

export function textoRecibo(venta: Venta, negocio: DatosNegocio, opciones: OpcionesRecibo = {}): string {
  const l: string[] = [];
  l.push(...encabezado(negocio));
  l.push(`Ticket ${venta.ticket}`);
  l.push(fecha(opciones.hora));
  if (opciones.cliente) l.push(...envolver(`Cliente: ${opciones.cliente.nombre}`));
  l.push('-'.repeat(ANCHO));

  for (const linea of venta.lineas) {
    l.push(...envolver(linea.descripcion));
    const presentacion =
      linea.unidadVentaId && linea.factor !== UNA_UNIDAD ? opciones.presentaciones?.get(linea.unidadVentaId) : undefined;
    const detalle = `  ${formatQuantity(linea.cantidad)}${presentacion ? ` ${presentacion}` : ''} x ${formatMoney(linea.precioUnitario, { symbol: false })}`;
    l.push(fila(detalle, formatMoney(linea.subtotal, { symbol: false })));
    // At an offer (2026-11-03): the list's price it would have been, under what it was.
    if (ahorroDeLinea({ ...linea, devuelta: undefined }) > 0) l.push(`  Oferta, antes ${formatMoney(linea.precioRegular!, { symbol: false })}`);
    if ((linea.devuelta ?? 0) > 0) l.push(`  Devuelto: ${formatQuantity(linea.devuelta!)}`);
  }

  l.push('-'.repeat(ANCHO));
  if (venta.descuento > 0) {
    l.push(fila('Descuento', `-${formatMoney(venta.descuento, { symbol: false })}`));
  }
  if ((venta.envio ?? 0) > 0) l.push(fila('Envío', formatMoney(venta.envio!, { symbol: false })));
  if (venta.redondeo !== 0) {
    l.push(fila('Redondeo', formatMoney(venta.redondeo, { symbol: false, signed: true })));
  }
  l.push(fila('TOTAL', formatMoney(venta.total)));

  for (const p of venta.pagos) {
    const etiqueta = FORMAS[p.forma] ?? p.forma;
    const monto =
      p.montoOriginal !== null && p.forma === 'efectivo_dolares'
        ? `US$ ${formatMoney(p.montoOriginal, { symbol: false })}`
        : formatMoney(p.monto, { symbol: false });
    l.push(fila(etiqueta, monto));
    if (p.estado === 'pendiente') l.push('  (transferencia sin confirmar)');
  }

  if (venta.cambio > 0) l.push(fila('Vuelto', formatMoney(venta.cambio, { symbol: false })));
  if ((venta.devuelto ?? 0) > 0) {
    l.push(fila('Devuelto', `-${formatMoney(venta.devuelto!, { symbol: false })}`));
  }

  // What the offers saved, on what was kept: the customer reads it last, as the shop's word.
  const ahorro = ahorroDe(venta.lineas);
  if (ahorro > 0) {
    l.push('-'.repeat(ANCHO));
    l.push(fila('Usted ahorró', formatMoney(ahorro)));
  }

  const saldo = opciones.cliente?.saldo;
  if (venta.pagos.some((p) => p.forma === 'credito') && saldo !== undefined && saldo !== null) {
    l.push('-'.repeat(ANCHO));
    l.push(
      saldo >= 0
        ? fila('Debe en total', formatMoney(saldo as Centavos))
        : fila('Saldo a favor', formatMoney(-saldo as Centavos)),
    );
  }

  l.push(...pie(negocio));
  return l.join('\n');
}

export interface OpcionesPedido {
  /** Who it goes to, and where: a delivery is taken to an address, and the driver may need to call. */
  cliente?: { nombre: string; direccion?: string | null; telefono?: string | null };
  /** Each product's name by id: the shop's own, as a sale's line keeps it. */
  productos: ReadonlyMap<string, string>;
  /** Each presentation's name and size by id: a line of anything but one base unit says which. */
  presentaciones?: ReadonlyMap<string, { nombre: string; factor: number }>;
}

/**
 * An order's receipt, before it is charged (the owner, 2026-10-12: «You should be able to print a
 * receipt for an order without having to complete the charge process and the receipt should have
 * the delivery fee in it somewhere»): the shop on top as on a sale's, the day it was taken where the
 * ticket goes, the day it is brought, who to and where, its lines at the prices agreed, the delivery
 * between them and the total, and that it is still to be paid. Charged, the till gives the sale's
 * own receipt, with its ticket and how it was paid.
 */
export function textoPedido(
  pedido: Pick<Pedido, 'fecha' | 'fechaEntrega' | 'lineas' | 'total' | 'envio'>,
  negocio: DatosNegocio,
  opciones: OpcionesPedido,
): string {
  const l: string[] = [];
  l.push(...encabezado(negocio));
  l.push(`Pedido del ${dia(pedido.fecha)}`);
  l.push(`Entrega: ${dia(pedido.fechaEntrega)}`);
  const cliente = opciones.cliente;
  if (cliente) {
    l.push(...envolver(`Cliente: ${cliente.nombre}`));
    if (cliente.direccion?.trim()) l.push(...envolver(`Dirección: ${cliente.direccion.trim()}`));
    if (cliente.telefono?.trim()) l.push(...envolver(`Tel: ${cliente.telefono.trim()}`));
  }
  l.push('-'.repeat(ANCHO));

  for (const linea of pedido.lineas) {
    l.push(...envolver(opciones.productos.get(linea.productoId) ?? 'Producto'));
    const unidad = opciones.presentaciones?.get(linea.unidadVentaId);
    const presentacion = unidad && unidad.factor !== UNA_UNIDAD ? unidad.nombre : undefined;
    const detalle = `  ${formatQuantity(linea.cantidad)}${presentacion ? ` ${presentacion}` : ''} x ${formatMoney(linea.precioUnitario, { symbol: false })}`;
    l.push(fila(detalle, formatMoney(linea.subtotal, { symbol: false })));
  }

  l.push('-'.repeat(ANCHO));
  const envio = pedido.envio ?? 0;
  if (envio > 0) l.push(fila('Envío', formatMoney(centavos(envio), { symbol: false })));
  l.push(fila('TOTAL', formatMoney(centavos(pedido.total + envio))));
  l.push('Pendiente de pago');

  l.push(...pie(negocio));
  return l.join('\n');
}

/**
 * A phone as WhatsApp wants it: country code and number, digits only.
 *
 * Nicaraguan numbers are eight digits and get written without the 505, the way everybody gives
 * them out — so eight digits get the 505 in front. A number written with + or 00 already says
 * its country. Anything else is not a number this can be sure of, and a fiado reminder sent to
 * a stranger is worse than picking the chat by hand, so it gets none.
 */
export function numeroWhatsApp(telefono: string): string | null {
  const escrito = telefono.trim();
  const digitos = escrito.replace(/\D/g, '');
  if (escrito.startsWith('+')) return digitos.length >= 8 ? digitos : null;
  if (digitos.startsWith('00')) return digitos.length >= 10 ? digitos.slice(2) : null;
  if (digitos.length === 8) return `505${digitos}`;
  if (digitos.length === 11 && digitos.startsWith('505')) return digitos;
  return null;
}

/**
 * wa.me works without any WhatsApp integration: it opens the app with the text prefilled — in
 * that person's chat when there is a number to go to, and on the chat list when there is not.
 */
export function enlaceWhatsApp(texto: string, telefono?: string): string {
  const numero = telefono ? numeroWhatsApp(telefono) : null;
  const base = numero ? `https://wa.me/${numero}` : 'https://wa.me/';
  return `${base}?text=${encodeURIComponent(texto)}`;
}

export function totalDe(lineas: readonly { subtotal: Centavos }[]): Centavos {
  return lineas.reduce<number>((a, l) => a + l.subtotal, 0) as Centavos;
}
